import { Tooltip, Tooltip as TooltipType } from 'primereact/tooltip';
import { Dropdown } from 'primereact/dropdown';
import { ColumnProps } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import { Chip, Table, Spinner, Typography } from '@uni-of-exeter/digital.lib.react.ui.kit';

import './resource-table.css';
import { APP_TEXT } from '../const/app-text';
import ContinueModal from './modals/continue-modal';
import ResourceTypeModal from './modals/resource-type';
import LikesInfoModal from './modals/likes-info-modal';
import { SortButton } from './resource-table/sort-button';
import ProgressInfoModal from './modals/progress-info-modal';
import ResourceLocationModal from './modals/resource-location';
import EvaluateResourceModal from './modals/evaluate-resource';
import { useResourceTable } from '../hooks/custom/use-resource-table';
import { locationCellTemplate } from '../utils/resource-table-helpers';
import HideRecommendationModal from './modals/hide-recommendation-modal';
import { BookmarkColumnBody } from './resource-table/bookmark-resource-column';
import { RemoveBookmarkColumnBody } from './resource-table/remove-bookmark-column';
import { ResourceTableColumnHeader } from './resource-table/resource-table-column-header';
import { ColumnHeaderInfoButton } from './resource-table/column-header-information-button';
import { useBookmarks } from '../hooks/custom/bookmarking/use-bookmarks';
import { useRef } from 'react';
import { HideRecommendationColumnBody } from './resource-table/hide-recommendation-column';
import { ResourceTableProps, EnhancedResourceTableData } from './shared/resource-table-types';

const text = APP_TEXT.components.resourceTable;
const externalResourceModal = APP_TEXT.components.externalResourceModal;

const sortOrderMap: Record<number, 'none' | 'ascending' | 'descending'> = {
  0: 'none',
  1: 'ascending',
  2: 'descending',
};

export const ResourceTable = ({
  resources,
  isLoading = false,
  isError = false,
  getCategoryColor,
  source,
  rowsToShow = 10,
  setCurrentPage,
  totalItems,
  currentPage,
  handleSort,
  sortField = undefined,
  sortOrder = 0,
  defaultPagination = false,
  enableRatingSystem = false,
  enableHideRecommendations = false,
  enableColumnSorting = false,
  enableBookmarkDeleteColumn = false,
  enableResourceBookmarking = false,
}: ResourceTableProps) => {
  const {
    // State
    progress,
    likeLoading,
    selectedExternalResource,
    evaluateResourceModalOpen,
    resourceToEvaluate,
    tableData,
    progressOptions,
    recommendationToHide,

    // Modal states
    resourceTypeModal,
    resourceLocationModal,
    progressModal,
    likesModal,
    hideRecommendationModal,

    // Event handlers
    handleLikeClick,
    handleProgressChange,
    handleViewClick,
    handleExternalContinue,
    getProgressDropdownClass,
    confirmHideRecommendation,

    // Setters
    setSelectedExternalResource,
    setEvaluateResourceModalOpen,
    setResourceToEvaluate,
    setRecommendationToHide,
  } = useResourceTable({
    resources,
    source,
    enableRatingSystem,
    getCategoryColor,
  });

  const {
    handleCreateBookmark,
    handleDeleteBookmark,
  } = useBookmarks();

  const tooltipRef = useRef<TooltipType | null>(null);

  const enhancedTableData: EnhancedResourceTableData[] = tableData.map((data) => ({
    ...data,
    titleString: data.title,
    title: (
      <a
        rel="noreferrer"
        className="resourceTitleUrl resourceTitleTarget"
        href={data.url}
        target="_blank"
        aria-label={data.title}
        tabIndex={0}
        onClick={(e) => {
          e.preventDefault();
          handleViewClick(data);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleViewClick(data);
          }
        }}
      >
        {source == 'DashboardPage' && data.isRoleDeptMatched === true && (
          <img
            src={text.roleDepartmentMatch.icon}
            alt={text.roleDepartmentMatch.alt}
          />
        )}
        {data.title}
      </a>
    ),
    category: (
      <Chip
        className="category"
        label={data.category}
        backgroundColor={getCategoryColor(data.category)}
        role="status"
        style={{ border: `1px solid ${getCategoryColor(data.category)}` }}
        aria-label={`Category: ${data.category}`}
      />
    ),
  }));

  const columns: ColumnProps[] = [
    {
      field: 'title',
      header: (
        <ResourceTableColumnHeader
          text={{
            headerText: text.headers.title,
          }}
        />
      ),
      style: {
        width: '294px',
      },
    },
    {
      field: 'category',
      header: (
        <ResourceTableColumnHeader
          text={{
            headerText: text.headers.digitalSkillsCategory,
          }}
        />
      ),
      bodyClassName: 'category-body',
      style: {
        width: '204px',
      },
    },
    {
      field: 'mediaType',
      header: (
        <th
          scope="col"
          className="resourceTableHeader"
          aria-sort="none"
        >
          <ColumnHeaderInfoButton
            text={{
              ariaLabel: text.accessibility.resourceTypeIIcon
            }}
            onClick={resourceTypeModal.onTrue}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                resourceTypeModal.onTrue();
              }
            }}
          />
          <Typography
            variant="body"
            className="resourceTableHeaderText"
          >
            {text.headers.resourceType}
          </Typography>
        </th>
      ),
      style: {
        width: '128px',
      },
    },
    {
      header: (
        <th
          scope="col"
          className="resourceTableHeader"
          aria-sort="none"
        >
          <ColumnHeaderInfoButton
            text={{
              ariaLabel: text.accessibility.resourceLocationIIcon
            }}
            onClick={resourceLocationModal.onTrue}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                resourceLocationModal.onTrue();
              }
            }}
          />
          <Typography
            variant="body"
            className="resourceTableHeaderText"
          >
            {text.headers.resourceLocation}
          </Typography>
        </th>
      ),
      body: (data: EnhancedResourceTableData) => (
        <Typography
          className="resourceTableColumnBodyText"
          variant="body"
        >
          {locationCellTemplate(data.isInternal, data.personalLogin)}
        </Typography>
      ),
      style: {
        width: '128px',
      },
    },
    {
      field: 'skillLevel',
      header: (
        <th
          scope="col"
          className="resourceTableHeader"
          aria-sort={sortField === 'skillLevel' ? sortOrderMap[sortOrder] : 'none'}
        >
          <Typography
            variant="body.xs"
            className="resourceTableHeaderText"
          >
            {text.headers.skillLevel}
          </Typography>
          {enableColumnSorting && (
            <div className="sortButtonContainer">
              {sortField === 'skillLevel' && (
                <SortButton
                  sortOrder={sortOrderMap[sortOrder]}
                  ariaLabel={text.accessibility.sortSkillLevelBtn}
                  onClick={() => handleSort?.('skillLevel')}
                />
              )}
              {sortField != 'skillLevel' && (
                <SortButton
                  sortOrder={'none'}
                  ariaLabel={text.accessibility.sortSkillLevelBtn}
                  onClick={() => handleSort?.('skillLevel')}
                />
              )}
            </div>
          )}
        </th>
      ),
      style: {
        width: '128px',
      },
    },
    {
      header: (
        <th
          scope="col"
          className="resourceTableHeader"
          aria-sort={sortField === 'userProgressStatus' ? sortOrderMap[sortOrder] : 'none'}
        >
          <ColumnHeaderInfoButton
            text={{
              ariaLabel: text.accessibility.progressIcon
            }}
            onClick={progressModal.onTrue}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                progressModal.onTrue();
              }
            }}
          />
          <Typography
            variant="body"
            className="resourceTableHeaderText"
          >
            {text.headers.progress}
          </Typography>
          {enableColumnSorting && (
            <div className="sortButtonContainer">
              {sortField === 'userProgressStatus' && (
                <SortButton
                  sortOrder={sortOrderMap[sortOrder]}
                  ariaLabel={text.accessibility.sortUserProgressStatusBtn}
                  onClick={() => handleSort?.('userProgressStatus')}
                />
              )}
              {sortField != 'userProgressStatus' && (
                <SortButton
                  sortOrder={'none'}
                  ariaLabel={text.accessibility.sortUserProgressStatusBtn}
                  onClick={() => handleSort?.('userProgressStatus')}
                />
              )}
            </div>
          )}
        </th>
      ),
      body: (data: EnhancedResourceTableData) => (
        <Dropdown
          value={progress[data.id] || 'NOT_STARTED'}
          options={progressOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(e) => handleProgressChange(data.id, e.value)}
          placeholder="Select"
          className={getProgressDropdownClass(progress[data.id] || 'NOT_STARTED')}
          appendTo={document.body}
          itemTemplate={(option) => (
            <div
              className="progressDropdownItem"
              key={option.value}
            >
              <Typography
                variant="body.xs"
                className="progressDropdownText"
              >
                {option.label}
              </Typography>
              <div className="dropdownSpacer" />
            </div>
          )}
        />
      ),
      style: {
        width: '128px',
      },
    },
  ];

  if (enableHideRecommendations) {
    columns.push({
      header: (
        <ResourceTableColumnHeader
          text={{
            headerText: text.headers.hideRecommendation,
          }}
        />
      ),
      body: (data: EnhancedResourceTableData) => (
        <HideRecommendationColumnBody
          onClick={(e) => {
            e.preventDefault();
            setRecommendationToHide(data.id);
            hideRecommendationModal.onTrue();
          }}
          onKeyDown={(e) => {
            e.preventDefault();
            if (e.key === 'Enter' || e.key === ' ') {
              setRecommendationToHide(data.id);
              hideRecommendationModal.onTrue();
            }
          }}
          text={{
            btnAriaLabel: text.accessibility.hideRecommendationIcon,
            tooltipText: text.tooltips.hideRecommendation,
          }}
        />
      ),
      style: {
        width: '54px',
      },
    });
  }

  if (enableRatingSystem) {
    columns.push({
      header: (
        <th
          scope="col"
          className="resourceTableHeader"
          aria-sort="none"
        >
          <ColumnHeaderInfoButton
            text={{
              ariaLabel: text.accessibility.likesIcon
            }}
            onClick={likesModal.onTrue}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                likesModal.onTrue();
              }
            }}
          />
          <Typography
            variant="body"
            className="resourceTableHeaderText"
          >
            {text.headers.likes}
          </Typography>
        </th>
      ),
      body: (data: EnhancedResourceTableData) => (
        <div
          id="ratingBtnWrapper"
          className="columnBtnWrapper"
        >
          <span
            className="ratingBox thumbIcon"
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onClick={() => handleLikeClick(data.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleLikeClick(data.id);
              }
            }}
          >
            <>
              <Typography
                variant="body.small"
                className="ratingsNumber"
              >
                {data.likeCount}
              </Typography>
              {likeLoading[data.id] ? (
                <Spinner
              className="spinner spinner-small"
                  strokeWidth="5"
                ></Spinner>
              ) : (
                <i className={`fa-${data.liked ? 'solid' : 'regular'} fa-thumbs-up thumbIcon`} />
              )}
            </>
          </span>
        </div>
      ),
      style: {
        width: '54px',
      },
    });
  }

  if (enableResourceBookmarking) {
    columns.push({
      header: (
        <ResourceTableColumnHeader
          text={{
            headerText: text.headers.bookmarkResource,
          }}
        />
      ),
      body: (data: EnhancedResourceTableData) => (
        <BookmarkColumnBody
          onClick={(event) => {
            event.preventDefault();

            if(!data.isBookmarked) {
              tooltipRef.current?.hide();

              handleCreateBookmark(data.id, data.titleString, data.isBookmarked);
            }
          }}         
          text={{
            btnToolTipText: `${data.isBookmarked? '' : text.tooltips.createBookmark}`,
            btnClassName: `bookmarkResourceBtn ${data.isBookmarked ? 'isActive' : ''}`,
            btnAriaLabel: data.isBookmarked 
              ? text.accessibility.removeBookmarkedResource 
              : text.accessibility.bookmarkResource
          }}
        />
      ),
      style: {
        width: '54px',
      },
    });
  }

  if (enableBookmarkDeleteColumn) {
    columns.push({
      header: (
        <ResourceTableColumnHeader
          text={{
            headerText: text.headers.removeResourceBookmark,
          }}
        />
      ),
      body: (data: EnhancedResourceTableData) => (
        <RemoveBookmarkColumnBody
          onClick={(event) => {
            event.preventDefault();
            handleDeleteBookmark(data.id, data.titleString, data.isBookmarked);
          }}
          text={{
            btnAriaLabel: text.accessibility.removeBookmarkedResource,
            btnClassName: "removeBookmarkBtn",
            btnTooltipText: text.tooltips.removeBookmark,
          }}
        />
      ),
      style: {
        width: '54px',
      },
    });
  }

  if (isLoading) {
    return (
      <div className="flexCenter">
        <Spinner aria-label={text.accessibility.loadingResourceSpinner} />
      </div>
    );
  }

  if (isError) {
    return <div>{text.error}</div>;
  }

  return (
    <div
      className="resourceTable"
      id="resourceTable"
    >
      <Table
        columns={columns}
        data={enhancedTableData}
        rowsPerPage={rowsToShow}
        pagination={defaultPagination}
        selectionMode="single"
      />
      {!defaultPagination && resources.length > 0 && (
        <Paginator
          className="resourceTableMobilePaginator"
          first={currentPage !== undefined ? currentPage * rowsToShow : 0}
          rows={rowsToShow}
          totalRecords={totalItems}
          onPageChange={(e) => setCurrentPage && setCurrentPage(e.page)}
        />
      )}
      <ResourceTypeModal
        isOpen={resourceTypeModal.value}
        onClose={resourceTypeModal.onFalse}
      />
      <ResourceLocationModal
        isOpen={resourceLocationModal.value}
        onClose={resourceLocationModal.onFalse}
      />
      <ProgressInfoModal
        isOpen={progressModal.value}
        onClose={progressModal.onFalse}
      />
      <LikesInfoModal
        isOpen={likesModal.value}
        onClose={likesModal.onFalse}
      />
      <HideRecommendationModal
        isOpen={hideRecommendationModal.value}
        onClose={hideRecommendationModal.onFalse}
        onConfirm={() => {
          confirmHideRecommendation(recommendationToHide);
        }}
      />
      <Tooltip 
        ref={tooltipRef} 
        target=".hasTooltip"
        appendTo={() => document.body}
        className="xxSmallTooltip"
      />
      <ContinueModal
        isOpen={selectedExternalResource !== null}
        onContinue={handleExternalContinue}
        onClose={() => setSelectedExternalResource(null)}
        title={externalResourceModal.title}
        body={externalResourceModal.body}
        primaryButtonText={externalResourceModal.continue}
        secondaryButtonText={externalResourceModal.back}
      />
      <EvaluateResourceModal
        isOpen={evaluateResourceModalOpen}
        onContinue={() => {
          if (resourceToEvaluate !== null) {
            handleLikeClick(resourceToEvaluate);
            setEvaluateResourceModalOpen(false);
            setResourceToEvaluate(null);
          }
        }}
        onClose={() => setEvaluateResourceModalOpen(false)}
      />
    </div>
  );
};
